import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core'
import { TranslatePipe } from '@ngx-translate/core'
import { SvgIconComponent } from 'angular-svg-icon'
import { format } from 'date-fns'
import { NzSwitchModule } from 'ng-zorro-antd/switch'
import { BehaviorSubject, Subscription } from 'rxjs'

import { TypedTemplateDirective } from '../../directives/typed-template.directive'
import { Address } from '../../models/address.model'
import { AddressHistory } from '../../models/records.model'
import { RecordsRepository } from '../../repositories/records/records.repository'

type AddressKey = keyof Omit<Address, 'id'>

type Attribute = {
  key: AddressKey
  leftValue?: string
  rightValue?: string
  latestValue: string
}

type FilterModel = {
  leftHistory?: string
  rightHistory?: string
}

@Component({
  selector: 'app-address-history',
  imports: [
    TranslatePipe,
    TypedTemplateDirective,
    NgTemplateOutlet,
    SvgIconComponent,
    NzSwitchModule,
    FormsModule,
    NgClass,
    FormlyModule,
    ReactiveFormsModule,
    DatePipe,
  ],
  templateUrl: './address-history.component.html',
})
export class AddressHistoryComponent implements OnInit, OnDestroy {
  @Input() private readonly id!: string

  public tableTypeToken!: { master: boolean; rows: Attribute[] }
  public keyTypeToken!: { master: boolean; key: string }
  public valueTypeToken!: { value: string; changed?: boolean }

  private addressSubscription!: Subscription
  private historySubscription!: Subscription
  public isLoading = true

  public showHistory?: boolean
  public showOnlyChanged?: boolean
  public masterData: Attribute[] = []
  public dynamicData: Attribute[] = []

  public fullHistory?: AddressHistory[]
  public leftHistory = new BehaviorSubject<AddressHistory[]>([])
  public rightHistory = new BehaviorSubject<AddressHistory[]>([])
  private leftHistoryVersion?: string
  private rightHistoryVersion?: string
  public isLeftLoading: boolean = true
  public isRightLoading: boolean = true

  public filterForm = new FormGroup({})
  public filterModel: FilterModel = {}
  public leftFilterField!: FormlyFieldConfig
  public rightFilterField!: FormlyFieldConfig

  public constructor(private readonly recordsRepository: RecordsRepository) {
    this.buildFormlyForm()
  }

  public ngOnInit(): void {
    this.getMainData()
    this.getHistory()
  }

  public ngOnDestroy(): void {
    this.addressSubscription?.unsubscribe()
    this.historySubscription?.unsubscribe()
  }

  private buildFormlyForm(): void {
    this.leftFilterField = {
      key: 'leftHistory',
      type: 'select',
      props: {},
    }
    this.leftHistory.subscribe((list) => {
      this.leftFilterField.props!.options = this.mapOptions(list)
      this.leftFilterField.props!.disabled = list.length <= 1
    })
    this.rightFilterField = {
      key: 'rightHistory',
      type: 'select',
      props: {},
    }
    this.rightHistory.subscribe((list) => {
      this.rightFilterField.props!.options = this.mapOptions(list)
      this.rightFilterField.props!.disabled = list.length <= 1
    })
  }

  private mapOptions(
    list: AddressHistory[]
  ): { label: string; value: string }[] {
    return list.map((val) => {
      const date = val.mergedAt
        ? format(new Date(val.mergedAt), 'dd.MM.yyyy - HH:mm')
        : '-'
      return {
        label: `${val.versionNumber}: ${date}`,
        value: val.referenceId,
      }
    })
  }

  private getMainData(): void {
    this.isLoading = true
    this.addressSubscription = this.recordsRepository
      .getById(this.id)
      .subscribe((res) => {
        const { masterData, dynamicData } = this.getAddressAttributes(
          res.data.attributes
        )
        this.masterData = [...masterData.entries()].map(([key, value]) => ({
          key: key,
          latestValue: value,
        }))
        this.dynamicData = [...dynamicData.entries()].map(([key, value]) => ({
          key: key,
          latestValue: value,
        }))
        this.isLoading = false
      })
  }

  private readonly masterDataFields: string[] = [
    'oewId',
    'uuId',
    'streetName',
    'houseNumber',
    'houseNumberAddition',
    'plz',
    'place',
    'district',
    'x',
    'y',
    'lat',
    'lon',
    'ags',
    'county',
    'marking',
    'parcelNumber',
    'markingId',
    'parcelIdentifier',
    'municipality',
    'municipalityId',
    'countyId',
  ]

  private getAddressAttributes(address: Omit<Address, 'id'>): {
    masterData: Map<AddressKey, string>
    dynamicData: Map<AddressKey, string>
  } {
    const masterData = new Map<AddressKey, string>()
    const dynamicData = new Map<AddressKey, string>()
    for (const key in address) {
      const attribute = key as AddressKey
      const value = address[attribute]
      const type = typeof value
      if (['string', 'number', 'boolean'].includes(type) || value === null) {
        if (this.masterDataFields.includes(attribute)) {
          masterData.set(attribute, `${value}`)
        } else if (
          ![
            'id',
            'prId',
            'status',
            'referenceId',
            'addressOewId',
            'createdAt',
            'updatedAt',
            'versionNumber',
            'isConflictAddress',
            'isUnverifiedAddress_',
            'isNewAddress',
            'isDuplicateWithChange',
            'isDuplicateInCsv',
          ].includes(attribute)
        ) {
          dynamicData.set(attribute, `${value}`)
        }
      }
    }
    return { masterData, dynamicData }
  }

  public onShowHistoryChange(value: boolean): void {
    this.showHistory = value
    if (value && !this.leftHistoryVersion) {
      const leftHistory = this.fullHistory!.at(-1)!.referenceId
      this.loadLeftData(leftHistory)
      this.filterModel.leftHistory = leftHistory
      this.filterForm.patchValue({ leftHistory })
      for (const attribute of this.masterData) {
        attribute.rightValue = attribute.latestValue
      }
      for (const attribute of this.dynamicData) {
        attribute.rightValue = attribute.latestValue
      }
      this.isRightLoading = false
    }
  }

  public onFilterChange(): void {
    const model = this.filterModel
    if (model.leftHistory) {
      this.loadLeftData(model.leftHistory)
    }
    if (model.rightHistory) {
      this.loadRightData(model.rightHistory)
    }
  }

  private loadLeftData(referenceId: string): void {
    if (this.leftHistoryVersion !== referenceId) {
      this.leftHistoryVersion = referenceId
      this.loadData('leftValue', referenceId)
      this.recalculateHistory()
    }
  }

  private loadRightData(referenceId: string): void {
    if (this.rightHistoryVersion !== referenceId) {
      this.rightHistoryVersion = referenceId
      this.loadData('rightValue', referenceId)
      this.recalculateHistory()
    }
  }

  private loadData(
    type: 'leftValue' | 'rightValue',
    referenceId: string
  ): void {
    if (type === 'leftValue') {
      this.isLeftLoading = true
    } else {
      this.isRightLoading = true
    }
    this.addressSubscription = this.recordsRepository
      .getById(referenceId)
      .subscribe((res) => {
        const { masterData, dynamicData } = this.getAddressAttributes(
          res.data.attributes
        )
        for (const attribute of this.masterData) {
          attribute[type] = masterData.get(attribute.key)
        }
        for (const attribute of this.dynamicData) {
          attribute[type] = dynamicData.get(attribute.key)
        }
        if (type === 'leftValue') {
          this.isLeftLoading = false
        } else {
          this.isRightLoading = false
        }
      })
  }

  private getHistory(): void {
    this.historySubscription = this.recordsRepository
      .getRecordsHistory(this.id)
      .subscribe((res) => {
        const history = res.data.map((item) => ({
          id: item.id,
          ...item.attributes,
        }))
        history.sort((a, b) => b.versionNumber - a.versionNumber)
        this.fullHistory = history

        if (history.length > 1) {
          this.rightHistory.next(history.slice(0, -1))
          const rightHistory = history[0].referenceId
          this.rightHistoryVersion = rightHistory
          this.filterModel.rightHistory = rightHistory
          this.filterForm.patchValue({ rightHistory })
        }
      })
  }

  private recalculateHistory(): void {
    const history = this.fullHistory!
    const leftIndex = history?.findIndex(
      (value) => value.referenceId === this.leftHistoryVersion
    )
    const rightIndex = history?.findIndex(
      (value) => value.referenceId === this.rightHistoryVersion
    )
    this.leftHistory.next(history.slice(rightIndex + 1))
    this.rightHistory.next(history.slice(0, leftIndex))
  }
}
